import React, { useEffect, useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { Moon, Sun, Menu, X, ChevronDown, Server, Layers, Container, HardDrive, ClipboardCheck, type LucideIcon } from "lucide-react";
import { useTheme } from "@/components/providers/ThemeProvider";
import logoUrl from "@/assets/logo.svg";
import { createPortal } from "react-dom";

interface DropdownItem {
  readonly href: string;
  readonly label: string;
  readonly description: string;
  readonly icon: LucideIcon;
}

const PRODUCT_ITEMS: readonly DropdownItem[] = [
  { href: "/products/ec2", label: "EC2", description: "Instance type optimization", icon: Server },
  { href: "/products/auto-scaling-groups", label: "Auto Scaling Groups", description: "Launch template optimization", icon: Layers },
  { href: "/products/ecs-fargate", label: "ECS Fargate", description: "Task CPU and memory sizing", icon: Container },
  { href: "/products/ebs", label: "EBS Volumes", description: "gp2 to gp3 migration", icon: HardDrive },
];

const SERVICE_ITEMS: readonly DropdownItem[] = [
  { href: "/services/cost-review", label: "AWS Cost Review", description: "One-off savings report", icon: ClipboardCheck },
];

const NAV_ITEMS: { href: string; label: string }[] = [
  { href: "/how-it-works", label: "How it works" },
  { href: "/pricing", label: "Pricing" },
  { href: "/docs", label: "Docs" },
];

function DesktopDropdown({
  label,
  items,
  isActive,
  currentPath,
}: {
  label: string;
  items: readonly DropdownItem[];
  isActive: boolean;
  currentPath: string;
}): React.ReactElement {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className={
          "flex items-center gap-1 text-base font-semibold hover:text-primary transition-colors" +
          (isActive ? " text-primary" : "")
        }
      >
        {label}
        <ChevronDown
          className={
            "size-4 transition-transform duration-200" +
            (open ? " rotate-180" : "")
          }
        />
      </button>

      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
          <div className="rounded-xl border bg-background/95 backdrop-blur shadow-lg p-1.5 min-w-64">
            {items.map(({ href, label: itemLabel, description, icon: Icon }) => (
              <Link
                key={href}
                href={href}
                className={
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary/5 hover:text-primary transition-colors" +
                  (currentPath === href ? " text-primary bg-primary/5" : "")
                }
              >
                <div className="size-7 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-none">
                  <Icon className="size-3.5" />
                </div>
                <div>
                  <div className="text-sm font-medium">{itemLabel}</div>
                  <div className="text-xs text-surface-foreground/50">{description}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileDropdown({
  label,
  items,
  isActive,
  currentPath,
  onNavigate,
}: {
  label: string;
  items: readonly DropdownItem[];
  isActive: boolean;
  currentPath: string;
  onNavigate: () => void;
}): React.ReactElement {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <button
        className={
          "flex items-center justify-between text-base font-semibold rounded px-3 py-2 hover:bg-primary/10 hover:text-primary w-full text-left" +
          (isActive ? " text-primary" : "")
        }
        onClick={() => setExpanded((v) => !v)}
      >
        {label}
        <ChevronDown
          className={
            "size-4 transition-transform duration-200" +
            (expanded ? " rotate-180" : "")
          }
        />
      </button>
      {expanded &&
        items.map(({ href, label: itemLabel, description, icon: Icon }) => (
          <Link
            key={href}
            href={href}
            className={
              "flex items-center gap-3 pl-6 pr-3 py-2 rounded hover:bg-primary/10 hover:text-primary transition-colors" +
              (currentPath === href ? " text-primary" : "")
            }
            onClick={onNavigate}
          >
            <div className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-none">
              <Icon className="size-3" />
            </div>
            <div>
              <div className="text-sm font-medium">{itemLabel}</div>
              <div className="text-xs text-surface-foreground/50">{description}</div>
            </div>
          </Link>
        ))}
    </>
  );
}

export function SiteHeader(): React.ReactElement {
  const { user } = usePage<{ user?: { id: number } }>().props;
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState("");
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const headerClass = [
    "fixed top-4 z-50 w-full transition-all duration-300",
    scrolled ? "px-4" : "-translate-y-4",
  ].join(" ");

  const innerClass = [
    "mx-auto w-full max-w-7xl rounded-xl border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
    scrolled ? "shadow-lg" : "rounded-none border-0 shadow-none",
  ].join(" ");

  const isProductsActive = currentPath.startsWith("/products/");
  const isServicesActive = currentPath.startsWith("/services/");

  return (
    <header className={headerClass}>
      <div className={innerClass}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          {/* Left: Logo */}
          <div className="flex items-center">
            <Link href="/" className="flex items-center space-x-2">
              <img
                src={logoUrl}
                alt="Infralyst Logo"
                width={32}
                height={32}
                className="w-auto h-9"
                loading="eager"
              />
              <span className="text-xl font-bold tracking-tight font-brand">
                infralyst
              </span>
            </Link>
          </div>

          {/* Center: Nav */}
          <nav className="items-center hidden lg:flex space-x-6 ml-8">
            <DesktopDropdown
              label="Products"
              items={PRODUCT_ITEMS}
              isActive={isProductsActive}
              currentPath={currentPath}
            />
            <DesktopDropdown
              label="Services"
              items={SERVICE_ITEMS}
              isActive={isServicesActive}
              currentPath={currentPath}
            />

            {/* Flat nav items */}
            {NAV_ITEMS.map(({ href, label }) => {
              const isActive = currentPath === href;
              return (
                <Link
                  key={href}
                  href={href}
                  className={
                    "text-base font-semibold hover:text-primary whitespace-nowrap" +
                    (isActive ? " text-primary" : "")
                  }
                >
                  {label}
                </Link>
              );
            })}
          </nav>

          {/* Right: Theme toggle + Auth CTAs */}
          <div className="flex items-center space-x-1 lg:space-x-3">
            {/* Desktop theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="hidden lg:inline-flex"
            >
              <Sun className="w-5 h-5 transition-all scale-100 rotate-0 dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute w-5 h-5 transition-all scale-0 rotate-90 dark:rotate-0 dark:scale-100" />
              <span className="sr-only">Toggle theme</span>
            </Button>
            {/* Desktop Auth CTAs */}
            {user ? (
              <>
                <Button asChild className="hidden lg:inline-flex">
                  <Link href="/workspaces">Workspaces</Link>
                </Button>
                <Button variant="outline" asChild className="hidden lg:inline-flex">
                  <Link href="/signout" method="delete" as="button">
                    Sign out
                  </Link>
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" asChild className="hidden lg:inline-flex">
                  <Link href="/signin">Sign in</Link>
                </Button>
                <Button asChild className="hidden lg:inline-flex">
                  <Link href="/signup">Start free</Link>
                </Button>
              </>
            )}
            {/* Mobile Hamburger */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </Button>
          </div>
        </div>

        {/* Mobile Menu Drawer */}
        {menuOpen &&
          createPortal(
            <div
              className="fixed inset-0 z-[9999] bg-black/40 dark:bg-black/60 backdrop-blur-sm transition-all"
              onClick={() => setMenuOpen(false)}
            >
              <div
                className="fixed top-0 right-0 w-72 h-full bg-white dark:bg-black shadow-lg flex flex-col p-6 space-y-4 transition-all"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button
                  className="absolute top-4 right-4 text-slate-500 hover:text-primary"
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close menu"
                >
                  <X className="w-6 h-6" />
                </button>
                {/* Auth CTAs at top */}
                <div className="flex flex-col space-y-2 mt-8">
                  {user ? (
                    <>
                      <Button asChild className="w-full">
                        <Link
                          href="/workspaces"
                          onClick={() => setMenuOpen(false)}
                        >
                          Workspaces
                        </Link>
                      </Button>
                      <Button variant="outline" asChild className="w-full">
                        <Link
                          href="/signout"
                          method="delete"
                          as="button"
                          onClick={() => setMenuOpen(false)}
                        >
                          Sign out
                        </Link>
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" asChild className="w-full">
                        <Link href="/signin" onClick={() => setMenuOpen(false)}>
                          Sign in
                        </Link>
                      </Button>
                      <Button asChild className="w-full">
                        <Link href="/signup" onClick={() => setMenuOpen(false)}>
                          Start free
                        </Link>
                      </Button>
                    </>
                  )}
                </div>
                {/* Theme toggle */}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setTheme(theme === "dark" ? "light" : "dark");
                    setMenuOpen(false);
                  }}
                >
                  {theme === "dark" ? (
                    <>
                      <Sun className="w-5 h-5 mr-2" /> Light Mode
                    </>
                  ) : (
                    <>
                      <Moon className="w-5 h-5 mr-2" /> Dark Mode
                    </>
                  )}
                </Button>
                {/* Nav links */}
                <nav className="flex flex-col space-y-1">
                  <MobileDropdown
                    label="Products"
                    items={PRODUCT_ITEMS}
                    isActive={isProductsActive}
                    currentPath={currentPath}
                    onNavigate={() => setMenuOpen(false)}
                  />
                  <MobileDropdown
                    label="Services"
                    items={SERVICE_ITEMS}
                    isActive={isServicesActive}
                    currentPath={currentPath}
                    onNavigate={() => setMenuOpen(false)}
                  />
                  {/* Flat items */}
                  {NAV_ITEMS.map(({ href, label }) => {
                    const isActive = currentPath === href;
                    return (
                      <Link
                        key={href}
                        href={href}
                        className={
                          "block text-base font-semibold rounded px-3 py-2 hover:bg-primary/10 hover:text-primary" +
                          (isActive ? " dark:text-white text-primary" : "")
                        }
                        onClick={() => setMenuOpen(false)}
                      >
                        {label}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </header>
  );
}
